import React, { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLanguage } from '../LanguageContext';
import T from '../components/T';

// The four About Me topics, in reading order. Each has a `.title` and
// `.content` under `homepage.about.items.*` in translations.tsx.
const ABOUT_ITEM_KEYS = ['ai', 'focus', 'entrepreneurial', 'creative'] as const;
type ItemKey = typeof ABOUT_ITEM_KEYS[number];

// Signature accent line sized to its title's rendered text width (the
// site-wide rule) — same ref + offsetWidth + 16px technique as
// Articles.tsx/Experience.tsx. Re-measured after web fonts load (Indie
// Flower is a different width from the fallback it would otherwise be
// measured against — the stale-width bug ArticleBase.tsx once had) and
// on resize.
function useTitleWidth(title: string) {
  const titleRef = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState('6rem'); // fallback until measured

  useEffect(() => {
    const measure = () => {
      // Runs to the tip of the expand arrow: the span's width already
      // includes the arrow's gap, and the arrow itself (0 at rest, 18px
      // open) is swapped for its full open width.
      const el = titleRef.current;
      if (el) {
        const arrowNow = parseFloat(getComputedStyle(el, '::after').width) || 0;
        setWidth(`${el.offsetWidth - arrowNow + 18}px`);
      }
    };
    measure();
    document.fonts?.ready.then(measure).catch(() => {});
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [title]);

  return [titleRef, width] as const;
}

// Only the desktop (md+) grid gets the "shuffle" choreography below — on
// a single mobile column every tile is already full-width and already
// stacked, so "expand to full width" and "regroup to three below" are
// both no-ops there; mobile keeps the simpler, pre-existing any-number-
// open-at-once accordion instead of forcing the FLIP machinery to do
// nothing. 768px matches Tailwind's default `md` breakpoint, the same one
// the grid's own `md:grid-cols-2` used to switch on.
function useIsDesktopGrid() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

interface AboutTileProps {
  title: string;
  titleNode: ReactNode;
  content: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

// Simple, mobile-only expand-in-place tile — any number open at once,
// content in its own half-width... actually full-width single column, no
// FLIP needed. This is the pre-"shuffle" behavior kept as-is for mobile;
// see AboutTileShuffleGrid below for the desktop picked direction.
const AboutTile: React.FC<AboutTileProps> = ({ title, titleNode, content, isOpen, onToggle }) => {
  const [titleRef, lineWidth] = useTitleWidth(title);

  return (
    <div className="about-tile expand-toggle p-3" data-expanded={isOpen || undefined}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="about-tile__toggle appearance-none bg-transparent border-none p-0 m-0 w-full text-left cursor-pointer"
      >
        <h3 className="text-xl md:text-2xl text-[var(--color-text-primary)] transition-colors duration-300" style={{fontFamily: 'Indie Flower, cursive'}}>
          <span ref={titleRef} className="about-tile__title expand-arrow inline-flex items-center">{titleNode}</span>
        </h3>
      </button>
      <div className="expand-panel">
        <div className="expand-panel-inner">
          <div className="mt-2">
            <div
              className="about-tile__line h-1 rounded-full mb-3"
              style={{ backgroundColor: 'var(--color-main-accent)', width: lineWidth }}
            ></div>
            <div className="expand-fade text-base lg:text-lg text-[var(--color-text-secondary)] transition-colors duration-300" style={{fontFamily: 'Short Stack, cursive'}}>
              {content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Desktop "Shuffle Down" grid — the picked direction from round 2 of
// /about-tile-expand-lab (now removed; see git history for the other 4
// compared directions, and for an earlier version of this grid that put
// the clicked tile's header above the regrouped three instead of below —
// title and its own body sandwiched an unrelated row of other options
// between them, which read as disjointed ("this is just weird") even
// though it kept the three reachable). Clicking a tile expands it to a
// full-width block; the other three regroup into an equal row of three
// directly ABOVE it, in their original relative order — same rule for
// whichever of the 4 tiles gets clicked, and unaffected by how long the
// expanded one's body text runs, since row 1 always comes before row 2
// regardless of row 2's height. Only one tile open at a time (a real
// behavior change from the old "any number open" — the regroup only
// reads cleanly with a single expanded tile). A 6-column CSS grid makes
// "3 of 6" (closed, 2 tiles/row) and "2 of 6" (open, 3 tiles/row) both
// exact thirds/halves; see gridPlacement().
//
// Grid-column/row aren't animatable on their own, so the actual motion
// is a measured FLIP (First-Last-Invert-Play) via useRowFlip: capture
// every tile's rect just before the click changes state, let the grid
// reflow, capture the new rects, instantly transform each tile back to
// where it used to be, then transition that transform to none. Once
// landed, the three regrouped tiles get a brief synchronized "tap
// square" pulse (a hand of cards knocked flush) — see the settle
// callback below. The reveal itself: the accent line draws in first
// (width 0 -> the title's own measured width, not a fixed stub, per the
// site's signature-line rule), then the body content's own top-level
// blocks (translations.tsx's content is consistently a single
// `<div className="flex flex-col gap-y-2">` of paragraphs/lists/bold
// lines) fade up with a per-block stagger — the same technique the
// site's very first About Me reveal used (`.about-reveal__body > * > *`,
// since replaced and now revived here in a different shape), scoped so
// it only ever plays while opening, not closing.
function gridPlacement(key: ItemKey, openKey: ItemKey | null): React.CSSProperties {
  const idx = ABOUT_ITEM_KEYS.indexOf(key);
  if (!openKey) {
    const row = Math.floor(idx / 2) + 1;
    const col = (idx % 2) * 3 + 1;
    return { gridColumn: `${col} / span 3`, gridRow: row };
  }
  if (key === openKey) {
    return { gridColumn: '1 / -1', gridRow: 2 };
  }
  const others = ABOUT_ITEM_KEYS.filter(k => k !== openKey);
  const oi = others.indexOf(key);
  return { gridColumn: `${oi * 2 + 1} / span 2`, gridRow: 1 };
}

function reducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface FlipOptions {
  onSettle?: (el: HTMLDivElement, key: ItemKey, index: number) => void;
}

// The FLIP engine: capture each tile's rect just before a click changes
// openKey (captureBefore, called synchronously inside the click handler,
// before setState), then once React has re-rendered into the new grid
// arrangement, measure again, invert the delta into an instant
// (no-transition) transform, force a reflow, and transition that
// transform back to identity. Correctness (every tile always ends up
// exactly where the grid says it should) is guaranteed by the browser's
// own layout engine.
function useRowFlip(openKey: ItemKey | null, opts: FlipOptions = {}) {
  const tileRefs = useRef<Partial<Record<ItemKey, HTMLDivElement | null>>>({});
  const beforeRects = useRef<Partial<Record<ItemKey, { left: number; top: number }>>>({});
  // Tracks whatever's currently in flight per tile (a pending rAF and/or
  // a live transitionend listener) so a new click — while a previous
  // FLIP cycle for that same tile hasn't finished starting yet — can
  // cancel it first. Without this, clicking a second tile before the
  // first click's requestAnimationFrame callback has fired leaves two
  // callbacks racing to set that tile's transform; the loser can set a
  // transform equal to what's already there (no property actually
  // changing), which never fires its own transitionend — leaving the
  // tile stuck mid-flight with a transform that never resolves.
  const pending = useRef<Partial<Record<ItemKey, { rafId?: number; cleanup?: (e?: TransitionEvent) => void; el?: HTMLDivElement; timeoutId?: number }>>>({});

  // Positions are measured relative to the grid, not the viewport: the
  // page itself can shift between the before/after measurements (scroll
  // anchoring, a smooth scroll in flight), and a viewport-relative delta
  // would fold that shift into every tile's flight — the whole page
  // seeming to slide along with the tiles.
  const relPos = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const g = el.parentElement!.getBoundingClientRect();
    return { left: r.left - g.left, top: r.top - g.top };
  };

  const captureBefore = () => {
    ABOUT_ITEM_KEYS.forEach(k => {
      const el = tileRefs.current[k];
      if (el) beforeRects.current[k] = relPos(el);
    });
  };

  const cancelPending = (key: ItemKey) => {
    const p = pending.current[key];
    if (!p) return;
    if (p.rafId) cancelAnimationFrame(p.rafId);
    if (p.timeoutId) window.clearTimeout(p.timeoutId);
    if (p.cleanup && p.el) p.el.removeEventListener('transitionend', p.cleanup);
    // Cancelling the callback alone isn't enough — if a click lands while
    // this tile's own rAF hasn't fired yet, the element is still sitting
    // at its *previous* cycle's instant-jump transform. Reset it to its
    // true, transform-free rest position now, synchronously, so the
    // upcoming getBoundingClientRect() in this new cycle measures where
    // the tile actually, really is.
    if (p.el) {
      p.el.style.transition = 'none';
      p.el.style.transform = '';
    }
    pending.current[key] = undefined;
  };

  useLayoutEffect(() => {
    const hadBefore = Object.keys(beforeRects.current).length > 0;
    const skip = reducedMotion();
    if (hadBefore && !skip) {
      ABOUT_ITEM_KEYS.forEach((k, i) => {
        const el = tileRefs.current[k];
        const before = beforeRects.current[k];
        cancelPending(k);
        if (!el || !before) return;
        const after = relPos(el);
        const dx = before.left - after.left;
        const dy = before.top - after.top;
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;

        el.style.transition = 'none';
        el.style.transform = `translate(${dx}px, ${dy}px)`;
        void el.offsetWidth; // force reflow so the instant jump actually paints first
        const rafId = requestAnimationFrame(() => {
          // will-change hints the browser to put this on its own
          // compositor layer for the duration of the flight — cheap
          // insurance against jank on a transform this large. The
          // "standard" Material easing curve (ease-out-weighted) reads
          // smoother here than a plain ease-in-out, which felt slightly
          // mechanical at this distance/duration.
          el.style.willChange = 'transform';
          el.style.transition = 'transform 620ms cubic-bezier(0.4, 0, 0.2, 1)';
          el.style.transform = 'translate(0px, 0px)';
          // transitionend BUBBLES: the tile's own children run their own
          // shorter transitions at the same moment (the expand-panel
          // height 0.3s, the accent line, the title's color), and the
          // first of those to finish used to trigger this cleanup at
          // ~200-300ms — clearing the transform mid-flight, so the tile
          // snapped to its final spot well before the 620ms fly was over.
          // That premature snap was the "flicker". Only the tile's own
          // transform transition counts; the timeout is a safety net for
          // a transform transition that never reports back.
          let timeoutId = 0;
          const cleanup = (e?: TransitionEvent) => {
            if (e && (e.target !== el || e.propertyName !== 'transform')) return;
            window.clearTimeout(timeoutId);
            el.style.transition = '';
            el.style.transform = '';
            el.style.willChange = '';
            el.removeEventListener('transitionend', cleanup);
            pending.current[k] = undefined;
            opts.onSettle?.(el, k, i);
          };
          el.addEventListener('transitionend', cleanup);
          timeoutId = window.setTimeout(() => cleanup(), 620 + 150);
          pending.current[k] = { cleanup, el, timeoutId };
        });
        pending.current[k] = { rafId, el };
      });
    }
    beforeRects.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  return { tileRefs, captureBefore };
}

interface ShuffleItem {
  key: ItemKey;
  title: string;
  titleNode: ReactNode;
  content: ReactNode;
}

// Title and body stay one combined tile again (title directly above its
// own content, no unrelated row sandwiched between them) — safe to do
// now that the regrouped three sit at row 1, ABOVE this tile's row 2,
// rather than between an earlier split header/body. Its own component
// (rather than inline JSX inside items.map() below) so useTitleWidth —
// one hook call per tile — stays rules-of-hooks compliant: each instance
// calls it unconditionally at its own top level, instead of a hook call
// living inside a loop within one shared component body.
const ShuffleTile: React.FC<{
  item: ShuffleItem;
  isOpen: boolean;
  isTapping: boolean;
  gridStyle: React.CSSProperties;
  onToggle: () => void;
  tileRef: (el: HTMLDivElement | null) => void;
}> = ({ item, isOpen, isTapping, gridStyle, onToggle, tileRef }) => {
  const [titleRef, lineWidth] = useTitleWidth(item.title);

  return (
    <div
      ref={tileRef}
      className={`about-tile expand-toggle about-shuffle-tile ${isTapping ? 'about-shuffle-tap' : ''}`}
      data-expanded={isOpen || undefined}
      style={gridStyle}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="about-tile__toggle appearance-none bg-transparent border-none p-0 m-0 w-full text-left cursor-pointer"
      >
        <h3 className="text-xl md:text-2xl text-[var(--color-text-primary)] transition-colors duration-300" style={{fontFamily: 'Indie Flower, cursive'}}>
          <span ref={titleRef} className="about-tile__title expand-arrow inline-flex items-center">{item.titleNode}</span>
        </h3>
      </button>
      <div className="expand-panel">
        <div className="expand-panel-inner">
          <div className="mt-2">
            <div
              className="about-tile__line about-shuffle-line h-1 rounded-full mb-3"
              style={{ backgroundColor: 'var(--color-main-accent)', width: isOpen ? lineWidth : '0' }}
            ></div>
            <div className="about-shuffle-body text-base lg:text-lg text-[var(--color-text-secondary)] transition-colors duration-300" style={{fontFamily: 'Short Stack, cursive'}}>
              {item.content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AboutTileShuffleGrid: React.FC<{ items: ShuffleItem[] }> = ({ items }) => {
  // Two keys, on purpose: `openKey` drives the grid arrangement (which
  // tiles sit where, i.e. what the FLIP animates), `expandedKey` drives
  // which tile's body is actually showing. They differ only during the
  // collapse-first step of toggle(): the body closes while the layout
  // stays put, and only then does the layout move.
  const [openKey, setOpenKey] = useState<ItemKey | null>(null);
  const [expandedKey, setExpandedKey] = useState<ItemKey | null>(null);
  const [tapping, setTapping] = useState(false);
  const moveTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(moveTimer.current), []);
  const { tileRefs, captureBefore } = useRowFlip(openKey, {
    onSettle: (_el, key) => {
      // Trigger the synchronized "tap square" pulse once, off the last of
      // the three "other" tiles to settle (they all share the same
      // duration/no stagger delay, so they finish together) — a cheap way
      // to land it once, not three times.
      const others = ABOUT_ITEM_KEYS.filter(k => k !== openKey);
      if (others.length && key === others[others.length - 1]) {
        setTapping(true);
        window.setTimeout(() => setTapping(false), 320);
      }
    },
  });

  // Something already open (switching tiles, or closing the open one):
  // collapse its body FIRST, in place, and only once it has closed let the
  // tiles fly to their new places. Doing both at once — the body shrinking
  // under tiles that are already moving — read as abrupt. Opening from the
  // all-closed state has nothing to collapse, so it moves straight away.
  // COLLAPSE_MS covers the .expand-panel height transition (0.3s).
  const COLLAPSE_MS = 340;
  const toggle = (key: ItemKey) => {
    const next = openKey === key ? null : key;
    window.clearTimeout(moveTimer.current);
    const move = () => {
      captureBefore();
      setOpenKey(next);
      setExpandedKey(next);
    };
    if (openKey && expandedKey && !reducedMotion()) {
      setExpandedKey(null);
      moveTimer.current = window.setTimeout(move, COLLAPSE_MS);
    } else {
      move();
    }
  };

  return (
    <div className="about-shuffle-grid">
      {items.map(item => {
        const isOpen = expandedKey === item.key;
        const isOther = openKey && openKey !== item.key;
        return (
          <ShuffleTile
            key={item.key}
            item={item}
            isOpen={isOpen}
            isTapping={!!(isOther && tapping)}
            gridStyle={gridPlacement(item.key, openKey)}
            onToggle={() => toggle(item.key)}
            tileRef={el => { tileRefs.current[item.key] = el; }}
          />
        );
      })}
    </div>
  );
};

// The grid itself — desktop gets the shuffle choreography above, mobile
// keeps the simpler pre-existing any-number-open accordion (see
// useIsDesktopGrid's comment for why). Same content-column width as the
// intro paragraph above (w-full sm:w-[80%] md:w-[60%]) — was wider (90%/75%) than
// that text block, so its left edge sat further out and read as shifted
// left rather than aligned under it.
const AboutTileGrid: React.FC = () => {
  const { t } = useLanguage();
  const isDesktop = useIsDesktopGrid();
  const [openKeys, setOpenKeys] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setOpenKeys(prev => ({ ...prev, [key]: !prev[key] }));

  const items: ShuffleItem[] = ABOUT_ITEM_KEYS.map(key => ({
    key,
    title: t(`homepage.about.items.${key}.title`),
    titleNode: <T k={`homepage.about.items.${key}.title`} />,
    content: <T k={`homepage.about.items.${key}.content`} />,
  }));

  return (
    <div className="w-full sm:w-[80%] md:w-[60%] mx-auto mt-6">
      {isDesktop ? (
        <AboutTileShuffleGrid items={items} />
      ) : (
        <div className="grid grid-cols-1 gap-y-8">
          {items.map(({ key, title, titleNode, content }) => (
            <AboutTile
              key={key}
              title={title}
              titleNode={titleNode}
              content={content}
              isOpen={!!openKeys[key]}
              onToggle={() => toggle(key)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AboutMe: React.FC = () => {

  return (
    <section className="py-24 bg-[var(--color-bg-section)] transition-colors duration-300" style={{fontFamily: 'Short Stack, cursive'}}>
      <div className="container mx-auto px-6">
        {/* Normal-height lead-in, statically shown — no scroll-triggered
            reveal. That wipe-in effect (title/line/paragraphs sweeping in
            via clip-path) was disliked wherever it showed up in this
            section; the tile grid below already replaced it for the four
            topics, and this intro no longer plays it either. */}
        <div>
          <h2 className="mb-4 text-2xl md:text-3xl text-center text-[var(--color-text-primary)] transition-colors duration-300" style={{fontFamily: 'Indie Flower, cursive'}}>
            <T k="homepage.about.title" />
          </h2>
          <div className="w-full sm:w-[80%] md:w-[60%] mx-auto">
            <div className="flex flex-col gap-y-2">
              <p className="text-base lg:text-lg text-[var(--color-text-secondary)] transition-colors duration-300" style={{fontFamily: 'Short Stack, cursive'}}>
                <T k="homepage.about.summary" />
              </p>
              <p className="text-base lg:text-lg text-[var(--color-text-secondary)] transition-colors duration-300" style={{fontFamily: 'Short Stack, cursive'}}>
                <T k="homepage.about.learnMore" />
              </p>
            </div>
          </div>
        </div>

        <AboutTileGrid />
      </div>
    </section>
  );
};

export default AboutMe;
